import { useState, useEffect, useCallback, useRef } from 'react';

type DockPosition = 'bottom' | 'right';

const STORAGE_KEY = 'redactit:results-panel-dock-position';

const DOCK_CONFIG = {
  bottom: { min: 150, max: 600, default: 256, cursor: 'row-resize' },
  right: { min: 200, max: 600, default: 320, cursor: 'col-resize' },
} as const;

export function useResultsPanelDock() {
  const [dockPosition, setDockPositionState] = useState<DockPosition>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'bottom' ? 'bottom' : 'right';
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [panelSize, setPanelSize] = useState<number>(DOCK_CONFIG[dockPosition].default);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const config = DOCK_CONFIG[dockPosition];

  const setDockPosition = useCallback((pos: DockPosition) => {
    setDockPositionState(pos);
    setPanelSize(DOCK_CONFIG[pos].default);
    localStorage.setItem(STORAGE_KEY, pos);
  }, []);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!panelRef.current) return;
      const containerRect = panelRef.current.parentElement?.getBoundingClientRect();
      if (!containerRect) return;

      let newSize: number;
      if (dockPosition === 'bottom') {
        newSize = containerRect.bottom - e.clientY;
      } else {
        newSize = containerRect.right - e.clientX;
      }

      setPanelSize(Math.max(config.min, Math.min(config.max, newSize)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = config.cursor;
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, dockPosition, config]);

  return {
    dockPosition,
    setDockPosition,
    isCollapsed,
    toggleCollapse,
    panelSize,
    isDragging,
    handleMouseDown,
    panelRef,
    config,
  };
}
